import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import logoImg from "@/assets/logo.png";

interface SplashScreenProps {
  onComplete: () => void;
}

/** Netflix-style cinematic intro chime via Web Audio API */
const playIntroSound = () => {
  try {
    const ctx = new AudioContext();
    const now = ctx.currentTime;

    // Master gain
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.18, now);
    master.connect(ctx.destination);

    // 1. Deep cinematic sub-bass rumble (builds tension)
    const sub = ctx.createOscillator();
    const subGain = ctx.createGain();
    sub.type = "sine";
    sub.frequency.setValueAtTime(55, now);
    sub.frequency.exponentialRampToValueAtTime(110, now + 1.5);
    subGain.gain.setValueAtTime(0, now);
    subGain.gain.linearRampToValueAtTime(0.35, now + 0.4);
    subGain.gain.linearRampToValueAtTime(0.2, now + 1.2);
    subGain.gain.linearRampToValueAtTime(0, now + 2.0);
    sub.connect(subGain).connect(master);
    sub.start(now);
    sub.stop(now + 2.0);

    // 2. Rising filtered noise sweep (whoosh)
    const noiseLen = 2 * ctx.sampleRate;
    const noiseBuffer = ctx.createBuffer(1, noiseLen, ctx.sampleRate);
    const noiseData = noiseBuffer.getChannelData(0);
    for (let i = 0; i < noiseLen; i++) noiseData[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuffer;
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = "bandpass";
    noiseFilter.frequency.setValueAtTime(200, now);
    noiseFilter.frequency.exponentialRampToValueAtTime(3000, now + 1.0);
    noiseFilter.frequency.exponentialRampToValueAtTime(800, now + 1.8);
    noiseFilter.Q.value = 1.5;
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0, now);
    noiseGain.gain.linearRampToValueAtTime(0.06, now + 0.3);
    noiseGain.gain.linearRampToValueAtTime(0.04, now + 1.0);
    noiseGain.gain.linearRampToValueAtTime(0, now + 1.8);
    noise.connect(noiseFilter).connect(noiseGain).connect(master);
    noise.start(now);
    noise.stop(now + 2.0);

    // 3. The signature chime — a two-note "ta-dum" like Netflix
    // Note 1: E5 (659Hz) — short, punchy
    const chime1 = ctx.createOscillator();
    const chime1Gain = ctx.createGain();
    chime1.type = "sine";
    chime1.frequency.value = 659.25; // E5
    chime1Gain.gain.setValueAtTime(0, now + 1.0);
    chime1Gain.gain.linearRampToValueAtTime(0.5, now + 1.05);
    chime1Gain.gain.exponentialRampToValueAtTime(0.15, now + 1.3);
    chime1Gain.gain.linearRampToValueAtTime(0, now + 1.5);
    chime1.connect(chime1Gain).connect(master);
    chime1.start(now + 1.0);
    chime1.stop(now + 1.5);

    // Note 1 harmonic (octave above, soft)
    const chime1h = ctx.createOscillator();
    const chime1hGain = ctx.createGain();
    chime1h.type = "sine";
    chime1h.frequency.value = 1318.5; // E6
    chime1hGain.gain.setValueAtTime(0, now + 1.0);
    chime1hGain.gain.linearRampToValueAtTime(0.12, now + 1.05);
    chime1hGain.gain.exponentialRampToValueAtTime(0.001, now + 1.4);
    chime1h.connect(chime1hGain).connect(master);
    chime1h.start(now + 1.0);
    chime1h.stop(now + 1.5);

    // Note 2: B5 (988Hz) — the resolve, rings longer
    const chime2 = ctx.createOscillator();
    const chime2Gain = ctx.createGain();
    chime2.type = "sine";
    chime2.frequency.value = 987.77; // B5
    chime2Gain.gain.setValueAtTime(0, now + 1.35);
    chime2Gain.gain.linearRampToValueAtTime(0.55, now + 1.4);
    chime2Gain.gain.exponentialRampToValueAtTime(0.08, now + 2.5);
    chime2Gain.gain.linearRampToValueAtTime(0, now + 3.2);
    chime2.connect(chime2Gain).connect(master);
    chime2.start(now + 1.35);
    chime2.stop(now + 3.2);

    // Note 2 harmonics (fifth + octave for richness)
    const chime2h = ctx.createOscillator();
    const chime2hGain = ctx.createGain();
    chime2h.type = "sine";
    chime2h.frequency.value = 1975.53; // B6
    chime2hGain.gain.setValueAtTime(0, now + 1.35);
    chime2hGain.gain.linearRampToValueAtTime(0.1, now + 1.4);
    chime2hGain.gain.exponentialRampToValueAtTime(0.001, now + 2.4);
    chime2h.connect(chime2hGain).connect(master);
    chime2h.start(now + 1.35);
    chime2h.stop(now + 2.5);

    // Fifth above note 2 (F#6)
    const chime2f = ctx.createOscillator();
    const chime2fGain = ctx.createGain();
    chime2f.type = "sine";
    chime2f.frequency.value = 1479.98; // F#6
    chime2fGain.gain.setValueAtTime(0, now + 1.38);
    chime2fGain.gain.linearRampToValueAtTime(0.06, now + 1.43);
    chime2fGain.gain.exponentialRampToValueAtTime(0.001, now + 2.2);
    chime2f.connect(chime2fGain).connect(master);
    chime2f.start(now + 1.38);
    chime2f.stop(now + 2.3);

    // 4. Reverb-like tail (long decay sine pad)
    const pad = ctx.createOscillator();
    const padGain = ctx.createGain();
    pad.type = "sine";
    pad.frequency.value = 493.88; // B4 (octave below resolve)
    padGain.gain.setValueAtTime(0, now + 1.4);
    padGain.gain.linearRampToValueAtTime(0.08, now + 1.6);
    padGain.gain.exponentialRampToValueAtTime(0.001, now + 3.5);
    pad.connect(padGain).connect(master);
    pad.start(now + 1.4);
    pad.stop(now + 3.5);

    setTimeout(() => ctx.close(), 4500);
  } catch {
    // Audio context not available — silent fallback
  }
};

const SPLASH_DURATION = 1800; // total ms before exit starts
const EXIT_DURATION = 800;

const SplashScreen = ({ onComplete }: SplashScreenProps) => {
  const [phase, setPhase] = useState<"enter" | "hold" | "exit">("enter");

  const stableComplete = useCallback(onComplete, []);

  useEffect(() => {
    playIntroSound();

    const holdTimer = setTimeout(() => setPhase("hold"), 300);
    const exitTimer = setTimeout(() => setPhase("exit"), SPLASH_DURATION);
    const doneTimer = setTimeout(() => stableComplete(), SPLASH_DURATION + EXIT_DURATION);

    return () => {
      clearTimeout(holdTimer);
      clearTimeout(exitTimer);
      clearTimeout(doneTimer);
    };
  }, [stableComplete]);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      animate={phase === "exit" ? { opacity: 0 } : { opacity: 1 }}
      transition={{ duration: EXIT_DURATION / 1000, ease: "easeInOut" }}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-background"
    >
      {/* Ambient glow — pulses gently */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: [0, 1.2, 1.5], opacity: [0, 0.3, 0.15] }}
        transition={{ duration: 2.5, ease: "easeOut" }}
        className="absolute w-96 h-96 rounded-full"
        style={{
          background: "radial-gradient(circle, hsl(var(--primary) / 0.5) 0%, hsl(var(--primary) / 0.1) 40%, transparent 70%)",
        }}
      />

      {/* Second ring — delayed, larger */}
      <motion.div
        initial={{ scale: 0.3, opacity: 0 }}
        animate={{ scale: [0.3, 1.6], opacity: [0, 0.3, 0] }}
        transition={{ duration: 2.2, ease: "easeOut", delay: 0.5 }}
        className="absolute w-64 h-64 rounded-full border border-primary/20"
      />

      {/* Inner ring */}
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: [0.5, 1.1], opacity: [0, 0.5, 0] }}
        transition={{ duration: 1.8, ease: "easeOut", delay: 0.2 }}
        className="absolute w-44 h-44 rounded-full border-2 border-primary/30"
      />

      {/* Logo group */}
      <motion.div
        initial={{ scale: 0.3, opacity: 0 }}
        animate={{
          scale: phase === "exit" ? 1.15 : 1,
          opacity: phase === "exit" ? 0 : 1,
        }}
        transition={{
          scale: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
          opacity: { duration: 0.5, ease: "easeOut" },
        }}
        className="relative z-10 flex flex-col items-center gap-7"
      >
        {/* Logo with glow effect */}
        <motion.div className="relative">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.6, 0.3] }}
            transition={{ duration: 1.5, delay: 0.8 }}
            className="absolute inset-0 blur-2xl"
            style={{ background: "hsl(var(--primary) / 0.3)" }}
          />
          <motion.img
            src={logoImg}
            alt="Encore Republic"
            fetchPriority="high"
            className="h-16 sm:h-20 object-contain drop-shadow-2xl relative z-10"
            initial={{ filter: "brightness(0)" }}
            animate={{ filter: "brightness(1)" }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          />
        </motion.div>

        {/* Tagline — fades in with the chime */}
        <motion.p
          initial={{ opacity: 0, y: 12, letterSpacing: "0.15em" }}
          animate={{ opacity: 0.6, y: 0, letterSpacing: "0.3em" }}
          transition={{ delay: 1.0, duration: 0.8, ease: "easeOut" }}
          className="text-sm font-display text-muted-foreground uppercase"
        >
          Share the Encore
        </motion.p>

        {/* Loading bar — synchronized with full duration */}
        <motion.div className="relative h-[2px] w-[140px] rounded-full bg-secondary/50 overflow-hidden">
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: "0%" }}
            transition={{ duration: SPLASH_DURATION / 1000 - 0.3, ease: [0.4, 0, 0.2, 1], delay: 0.3 }}
            className="absolute inset-0 bg-gradient-to-r from-primary/40 via-primary to-primary/40 rounded-full"
          />
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default SplashScreen;
